<template>
  <div class="relative h-60 w-full bg-kekkle-red-light">
    <transition v-if="!loadedFromCache" name="opacity">
      <img
        v-if="pictureUrl"
        alt="group-picture"
        draggable="false"
        class="relative z-20 mx-auto h-full w-full object-cover object-center"
        :src="pictureUrl"
        @click="openImage"
      />
      <img
        v-else-if="!pictureUrl && blurUrl"
        alt="group-picture"
        draggable="false"
        class="relative z-20 mx-auto h-full w-full object-cover"
        :src="blurUrl"
      />
      <div v-else class="z-40 h-full w-full bg-kekkle-red"></div>
    </transition>
    <template v-else>
      <img
        v-if="pictureUrl"
        alt="group-picture"
        draggable="false"
        class="relative z-20 mx-auto h-full w-full object-cover object-center"
        :src="pictureUrl"
        @click="openImage"
      />
      <img
        v-else-if="!pictureUrl && blurUrl"
        alt="group-picture"
        draggable="false"
        class="relative z-20 mx-auto h-full w-full object-cover"
        :src="blurUrl"
      />
      <div v-else class="z-40 h-full w-full bg-kekkle-red"></div>
    </template>

    <div
      class="absolute right-2 bottom-2 z-20 flex min-w-max transform cursor-pointer flex-col items-center rounded-sm bg-white p-1 transition-transform hover:-translate-y-1"
      @click="$router.push({ name: 'groupProfileSettings' })"
    >
      <upload-icon
        :class="[!hasImageSet ? 'h-6 w-6 animate-bounce' : 'h-6 w-6']"
      >
      </upload-icon>
      <p v-if="!hasImageSet" class="w-full px-1">
        {{ $t("pages.group.upload_photo") }}
      </p>
    </div>
  </div>
  <div
    v-show="fullscreen"
    class="fixed top-0 left-0 z-50 flex min-h-screen w-screen flex-col items-center justify-center overflow-scroll bg-slate-700/60"
    @click="closeImage"
  >
    <img
      v-if="pictureUrl"
      ref="zoomImage"
      alt="Foto-big"
      :src="pictureUrl"
      style="max-height: 50vh"
      class="h-full max-w-lg overflow-visible rounded-sm border-2 border-black-dark object-contain"
      :class="hasFullHdImage ? '' : 'animate-pulse'"
      @click.stop
      @touchstart="touchStart"
      @touchmove="touchMove"
    />
  </div>
</template>
<script lang="ts">
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { UploadIcon } from "@heroicons/vue/outline";
import { decodeBlurhashToUrl } from "@/helpers/blurhash/decodeBlurhashToUrl";
import { ImageSizes } from "@/enums/images-sizes";

export default {
  name: "GroupHeaderThumb",
  components: {
    UploadIcon,
  },
  setup() {
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    return { groupsStore, userStore };
  },
  data() {
    return {
      blurUrl: "" as string | null,
      fullscreen: false,
      start: {
        x: 0,
        y: 0,
        dx: 0,
        dy: 0,
        distance: 0,
        currentScale: 1,
      },
    };
  },
  computed: {
    selectedGroup() {
      return this.userStore.activeGroup;
    },
    hasFullHdImage() {
      return !!this.groupPictureData?.[ImageSizes.HD]?.["url"];
    },
    hasImageSet() {
      return !!this.groupsStore.groupData?.group_picture;
    },
    groupPictureData() {
      return this.groupsStore.activeGroupPictureData;
    },
    pictureUrl() {
      return (
        this.groupPictureData?.[ImageSizes.HD]?.url ??
        this.groupPictureData?.[ImageSizes.SD]?.url
      );
    },
    loadedFromCache() {
      return (
        this.groupPictureData?.[ImageSizes.HD]?.fromCache ??
        this.groupPictureData?.[ImageSizes.SD]?.fromCache ??
        false
      );
    },
  },
  watch: {
    async selectedGroup() {
      // Only download if not already loaded
      if (!this.groupPictureData?.[ImageSizes.SD]?.url) {
        this.groupsStore.downloadGroupPictureData(ImageSizes.SD);
      }
      if (!this.groupPictureData?.[ImageSizes.HD]?.url) {
        this.groupsStore.downloadGroupPictureData(ImageSizes.HD);
      }
      this.blurUrl = decodeBlurhashToUrl(this.groupPictureData?.blurhash ?? "");
    },
  },
  created() {
    // Only download if not already loaded
    if (!this.groupPictureData?.[ImageSizes.SD]?.url) {
      this.groupsStore.downloadGroupPictureData(ImageSizes.SD);
    }
    if (!this.groupPictureData?.[ImageSizes.HD]?.url) {
      this.groupsStore.downloadGroupPictureData(ImageSizes.HD);
    }
    this.blurUrl = decodeBlurhashToUrl(this.groupPictureData?.blurhash ?? "");
  },
  methods: {
    openImage() {
      if (!this.hasFullHdImage) {
        this.groupsStore.downloadGroupPictureData(ImageSizes.Full_hd);
      }
      this.fullscreen = true;
    },
    closeImage() {
      this.fullscreen = false;
      const zoomImage = this.$refs.zoomImage as HTMLImageElement | undefined;
      if (zoomImage) {
        zoomImage.style.webkitTransform = "";
        zoomImage.style.transform = "";
        zoomImage.style.zIndex = "";
      }
      this.start = {
        x: 0,
        y: 0,
        dx: 0,
        dy: 0,
        distance: 0,
        currentScale: 1,
      };
    },
    distance(event: TouchEvent) {
      if (!event.touches[0] || !event.touches[1]) return 0;
      return Math.hypot(
        event.touches[0].pageX - event.touches[1].pageX,
        event.touches[0].pageY - event.touches[1].pageY,
      );
    },
    touchStart(event: TouchEvent) {
      if (event.touches.length === 2 && event.touches[0] && event.touches[1]) {
        event.preventDefault(); // Prevent page scroll
        // Calculate where the fingers have started on the X and Y axis
        this.start.x =
          (event.touches[0].pageX + event.touches[1].pageX) / 2 - this.start.dx;
        this.start.y =
          (event.touches[0].pageY + event.touches[1].pageY) / 2 - this.start.dy;
        this.start.distance = this.distance(event);
      }
    },
    touchMove(event: TouchEvent) {
      if (event.touches.length === 2 && event.touches[0] && event.touches[1]) {
        event.preventDefault();
        let scale;
        if ("scale" in event) {
          scale = (event as any).scale;
        } else {
          scale = this.distance(event) / this.start.distance;
        }
        let imageElementScale = Math.min(
          Math.max(0.8, scale * this.start.currentScale),
          10,
        );

        // Calculate how much the fingers have moved on the X and Y axis
        const deltaX =
          event.touches[0].pageX + event.touches[1].pageX / 2 - this.start.x;
        const deltaY =
          event.touches[0].pageY + event.touches[1].pageY / 2 - this.start.y;
        this.start.dx = deltaX;
        this.start.dy = deltaY;

        // Transform the image to make it grow and move with fingers
        const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`;

        const zoomImage = this.$refs.zoomImage as HTMLImageElement | undefined;
        if (zoomImage) {
          zoomImage.style.transform = transform;
          zoomImage.style.webkitTransform = transform;
          zoomImage.style.zIndex = "9999";
        }
      }
    },
  },
};
</script>
<style scoped>
.opacity-enter-active {
  transition: opacity 0.5s ease;
}

.opacity-leave-active {
  position: absolute;
  transition: opacity 0.5s ease;
}
.opacity-enter, .opacity-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
